import {
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Maximize2,
  Minus,
  Plus,
  ZoomIn,
  ZoomOut,
} from "lucide-react"

import { cn } from "@/lib/utils"

import type { Finding, FindingId } from "../data/review-data"
import { FindingHoverChip, PillButton, PillDivider } from "./pdf-controls-parts"

/**
 * Zoom moves in even tenths between a floor and a ceiling.
 *
 * A ramp of named stops (50, 75, 100, 125, 150, 200) jumps by a different
 * amount at every press, so the reviewer cannot anticipate where the next click
 * lands and overshoots the size they wanted. A constant step is predictable in
 * both directions and reads as continuous.
 *
 * The floor is where a page stops being readable; the ceiling is well past the
 * point where a finding's own framing is the better tool.
 */
const ZOOM_STEP = 0.1
export const ZOOM_MIN = 0.5
export const ZOOM_MAX = 4

/** Hold a zoom inside the readable range, rounded off floating-point drift. */
export function clampZoom(zoom: number): number {
  return Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom)) * 100) / 100
}

export interface PdfControlsProps {
  findings: Finding[]
  activeFindingId: FindingId | null
  onSelectFinding: (id: FindingId) => void
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  /**
   * Whether finding marks are drawn on the page, and how to flip that.
   *
   * A label under thirty marks is hard to read as a label, and checking the
   * artwork as printed is part of reviewing it. Omitting `onToggleBoxes` omits
   * the control: a pane with no marks has nothing to hide.
   */
  showBoxes?: boolean
  onToggleBoxes?: () => void
  /**
   * Tighten the controls for a narrow pane.
   *
   * The compare view puts a full set of controls over each of two panes, so
   * the chrome that reads as generous over one document reads as clutter over
   * two.
   */
  compact?: boolean
  /** Frame the active finding: bump zoom + scroll its bbox into view. */
  onZoomToActive?: () => void
  /** True while framed on the active finding — the button offers to zoom out. */
  isFramed?: boolean
  className?: string
}

export function PdfControls({
  findings,
  activeFindingId,
  onSelectFinding,
  page,
  pageCount,
  onPageChange,
  showBoxes = true,
  onToggleBoxes,
  compact = false,
  onZoomToActive,
  isFramed = false,
  className,
}: PdfControlsProps): JSX.Element {
  const activeIndex = findings.findIndex((f) => f.id === activeFindingId)
  const active = activeIndex >= 0 ? findings[activeIndex] : null

  const step = (delta: -1 | 1) => {
    if (findings.length === 0) return
    const next =
      activeIndex < 0
        ? delta === 1
          ? 0
          : findings.length - 1
        : (activeIndex + delta + findings.length) % findings.length
    const target = findings[next]
    if (target) onSelectFinding(target.id)
  }

  return (
    /* Two pills rather than one bar: moving between findings and changing the
       zoom are different jobs, and a single run of controls makes the reviewer
       read the whole strip to find either. The gap is what separates them —
       until the pane is too narrow to afford it, where `compact` closes the gap
       so two panes of controls do not outweigh the documents they steer. */
    <div
      className={cn(
        "pointer-events-none flex items-center",
        compact ? "gap-1" : "gap-2",
        className,
      )}
    >
      <div
        className={cn(
          "pointer-events-auto inline-flex items-center gap-1 rounded-full border border-border bg-surface/95 text-text-2 shadow-e3 backdrop-blur",
          compact ? "h-9 px-1.5" : "h-11 px-2",
        )}
      >
        <PillButton
          label="Previous finding"
          onClick={() => step(-1)}
          disabled={findings.length === 0}
        >
          <ChevronUp className="size-4" aria-hidden="true" />
        </PillButton>

        <div
          className={cn(
            "flex items-center justify-center gap-1.5 tabular-nums text-text-2",
            // Fixed, not a minimum: a pill that grows with the claim resizes under the cursor.
            compact ? "w-[220px] px-2 text-[11.5px]" : "w-[300px] px-2.5 text-body-sm",
          )}
        >
          {active ? (
            /* Show the finding's stable number (what appears on the box badge),
               not its index in the rail order — otherwise the pill reads "3"
               while the badge in the gutter reads "1". */
            <FindingHoverChip finding={active} />
          ) : (
            <span className="text-text-3">
              {findings.length} finding{findings.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        <PillButton label="Next finding" onClick={() => step(1)} disabled={findings.length === 0}>
          <ChevronDown className="size-4" aria-hidden="true" />
        </PillButton>

        {findings.length > 0 && (
          /* 0-based index; a dash before anything is selected, since 0 reads as a count. */
          <span className="shrink-0 pr-1 font-id text-caption tabular-nums text-text-3">
            {activeIndex >= 0 ? activeIndex + 1 : "–"}/{findings.length}
          </span>
        )}

        {onZoomToActive && active?.bbox && (
          <PillButton
            label={isFramed ? "Reset zoom" : "Zoom in on this finding"}
            onClick={onZoomToActive}
          >
            {isFramed ? (
              <ZoomOut className="size-4" aria-hidden="true" />
            ) : (
              <ZoomIn className="size-4" aria-hidden="true" />
            )}
          </PillButton>
        )}

        {onToggleBoxes && (
          <>
            <PillDivider />
            <PillButton
              label={showBoxes ? "Hide finding marks" : "Show finding marks"}
              onClick={onToggleBoxes}
            >
              {showBoxes ? (
                <Eye className="size-4" aria-hidden="true" />
              ) : (
                <EyeOff className="size-4" aria-hidden="true" />
              )}
            </PillButton>
          </>
        )}

        {pageCount > 1 && (
          <>
            <PillDivider />
            <PillButton
              label="Previous page"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
            >
              <ChevronUp className="size-4" aria-hidden="true" />
            </PillButton>
            <span className="px-1 font-id text-[10.5px] tabular-nums text-text-2">
              {page} / {pageCount}
            </span>
            <PillButton
              label="Next page"
              onClick={() => onPageChange(Math.min(pageCount, page + 1))}
              disabled={page >= pageCount}
            >
              <ChevronDown className="size-4" aria-hidden="true" />
            </PillButton>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Zoom for one document pane.
 *
 * Its own control rather than a segment of the navigator: a reviewer steps
 * through findings with one hand and adjusts magnification rarely, so the two
 * sit apart — and in the compare view the navigator has to stay narrow enough
 * to float over a half-width pane without being clipped.
 */
export function PdfZoomControl({
  zoom,
  onZoomChange,
  onToggleFullscreen,
  className,
}: {
  zoom: number
  onZoomChange: (zoom: number) => void
  onToggleFullscreen?: () => void
  className?: string
}): JSX.Element {
  const zoomStep = (delta: -1 | 1) => {
    // Snap to the step grid before moving, so a zoom arrived at by framing a
    // finding (any value at all) rejoins the ramp rather than carrying its
    // remainder forward through every later press.
    const onGrid = Math.round(zoom / ZOOM_STEP) * ZOOM_STEP
    const next = clampZoom(onGrid + delta * ZOOM_STEP)
    if (next !== zoom) onZoomChange(next)
  }

  return (
    <div
      className={cn(
        "pointer-events-auto inline-flex h-9 items-center gap-1 rounded-full border border-border bg-surface/95 px-1.5 text-text-2 shadow-e3 backdrop-blur",
        className,
      )}
    >
      <PillButton label="Zoom out" onClick={() => zoomStep(-1)}>
        <Minus className="size-4" aria-hidden="true" />
      </PillButton>
      <button
        type="button"
        onClick={() => onZoomChange(1)}
        className="rounded-full px-2 font-id text-body-sm tabular-nums text-text-2 transition-colors duration-120 ease-out hover:bg-surface-sunken hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
        aria-label="Reset zoom to 100%"
      >
        {Math.round(zoom * 100)}%
      </button>
      <PillButton label="Zoom in" onClick={() => zoomStep(1)}>
        <Plus className="size-4" aria-hidden="true" />
      </PillButton>
      {onToggleFullscreen && (
        <>
          <PillDivider />
          <PillButton label="Fullscreen" onClick={onToggleFullscreen}>
            <Maximize2 className="size-4" aria-hidden="true" />
          </PillButton>
        </>
      )}
    </div>
  )
}

/**
 * A ghost button sized for the floating pill. Not the same as IconButton
 * because the pill needs a lighter footprint (26px square, transparent
 * unless hovered).
 */
